import { ManualDepositProcess, ManualProcessStatusType, PermissionType } from '@golden/gdk-admin'
import { makeStyles } from '@material-ui/core/styles'
import { Link } from 'react-router-dom'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Tooltip from '@material-ui/core/Tooltip'
import ErrorIcon from '@material-ui/icons/Error'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { Path } from '../route/route'
import { useChecker } from '../../../utils/admin/AdminRouteHook'
import React, { useEffect, useMemo, useState } from 'react'
import { useDialogHandleClick, useGetData, useReload } from '../../../utils/default/ComplexFlowHook'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import DateTime from '../../../components/default/present/DateTime'
import manualProcessStatusName from '../../../constants/admin/manualProcessStatusName'
import manualProcessStatusColor from '../../../constants/admin/manualProcessStatusColor'
import Button from '@material-ui/core/Button'
import { createTableData } from '../../../utils/default/TableHelper'
import LoadingAndErrorFrame from '../../../components/default/frames/LoadingAndErrorFrame'
import CoreTable from '../../../components/default/present/CoreTable'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { timer } from 'rxjs'

export interface RowType {
  id: number | string
  order: number | '-'
  name: string
  count: number
  operator: string
  uploadAt: React.ReactElement
  status: React.ReactElement
  operation: React.ReactElement | '-'
}

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.error.main,
    marginLeft: 2
  }
}))

const DeleteButton: React.FC<{ id: number, reload: () => void }> = (props) => {
  const { id, reload } = props
  const { t } = useT()
  const commonClasses = useCommonStyles()
  const gdk = useGDK()
  const globalDialog = useGlobalDialog()

  const handleDelete = useDialogHandleClick({
    dialogId: `deleteManualDepositProcess-${id}`,
    globalDialog,
    changeDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.confirmDelete')
    }),
    successDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.deleteFinish'),
      showCancel: false
    }),
    getFailDialogConfig: (error: string) => createGlobalDialogConfig({
      variant: 'error',
      showIcon: true,
      message: error,
      showCancel: false
    }),
    payload: id,
    gdkFunc: (payload) => gdk.manual.deleteDepositProcess(payload),
    gdkFuncDependencies: [gdk],
    onSuccess: () => { reload() },
    onSuccessDependencies: [reload]
  })
  return (
    <Button
      size="small"
      className={commonClasses.blueGradualOutlineButton}
      onClick={handleDelete}
    >
      {t('common.delete')}
    </Button>
  )
}

const RetryButton: React.FC<{ id: number, reload: () => void }> = (props) => {
  const { id, reload } = props
  const { t } = useT()
  const commonClasses = useCommonStyles()
  const gdk = useGDK()
  const globalDialog = useGlobalDialog()

  const handleRetry = useDialogHandleClick({
    dialogId: `retryManualDepositProcess-${id}`,
    globalDialog,
    changeDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.confirmRetry')
    }),
    successDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.retryFinish'),
      showCancel: false
    }),
    getFailDialogConfig: (error: string) => createGlobalDialogConfig({
      variant: 'error',
      showIcon: true,
      message: error,
      showCancel: false
    }),
    payload: id,
    gdkFunc: (payload) => gdk.manual.retryDepositProcess(payload),
    gdkFuncDependencies: [gdk],
    onSuccess: () => { reload() },
    onSuccessDependencies: [reload]
  })
  return (
    <Button
      size="small"
      className={commonClasses.blueGradualOutlineButton}
      onClick={handleRetry}
    >
      {t('common.retry')}
    </Button>
  )
}

const ManualDepositProcessTable: React.FC = () => {
  const { t } = useT()
  const classes = useStyles()
  const gdk = useGDK()
  const writableEdit = useChecker([PermissionType.MANUAL_DEPOSIT])
  const pageFlow = usePageFlow()
  const commonClasses = useCommonStyles()
  const [list, setList] = useState<ManualDepositProcess[]>([])
  const [failedList, setFailedList] = useState<ManualDepositProcess[]>([])
  const { reload, reloadFlag } = useReload()

  const tableClasses = useMemo(() => ({
    head: commonClasses.pinkTableHead,
    row: commonClasses.tableRow,
    cellHead: commonClasses.tableCellHead,
    cellBody: commonClasses.pre
  }), [commonClasses])

  useGetData({
    gdkFunc: () => gdk.manual.getDepositProcesses(),
    gdkFuncDependencies: [reloadFlag],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: { list: ManualDepositProcess[], failed_list: ManualDepositProcess[] }) => {
      setList(res.list)
      setFailedList(res.failed_list)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError
  })

  const rows: RowType[] = useMemo(() => [
    ...failedList.slice().reverse().map((item) => ({ ...item, order: '-' })),
    ...list.slice().reverse().map((item, index) => ({ ...item, order: index + 1 }))
  ].map((item) => {
    return {
      id: `${item.status}-${item.id}`,
      order: item.order,
      name: item.name,
      count: item.count,
      operator: item.operator,
      uploadAt: (<DateTime time={item.upload_at} />),
      status: (
        <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center">
          <span className={commonClasses.chipText} style={{ backgroundColor: manualProcessStatusColor[item.status] }}>
            { t(manualProcessStatusName[item.status]) }
          </span>
          { item.failed_log && (
            <Tooltip title={(
              <div className={commonClasses.pre}>{item.failed_log}</div>
            )}>
              <ErrorIcon className={classes.icon} />
            </Tooltip>
          ) }
        </Box>
      ),
      operation: item.status === ManualProcessStatusType.FAILED
        ? (
          <Box display='flex' justifyContent='center'>
            <Box>
              <DeleteButton id={item.id} reload={reload} />
            </Box>
            <Box marginLeft={1}>
              <RetryButton id={item.id} reload={reload} />
            </Box>
          </Box>
          )
        : '-'
    } as RowType
  }), [commonClasses.chipText, failedList, list, reload, t, classes, commonClasses.pre])

  const data = useMemo(() => {
    return createTableData<RowType>(
      {
        id: {
          label: '',
          value: 'id'
        },
        order: {
          label: t('common.queueOrder'),
          value: 'order',
          align: 'center'
        },
        name: {
          label: t('common.filenameAndAccount'),
          value: 'name',
          align: 'center'
        },
        count: {
          label: t('common.amount'),
          value: 'count',
          align: 'center'
        },
        operator: {
          label: t('common.updateBy'),
          value: 'operator',
          align: 'center'
        },
        uploadAt: {
          label: t('common.updateAt'),
          value: 'uploadAt',
          align: 'center'
        },
        status: {
          label: t('common.process'),
          value: 'status',
          align: 'center'
        },
        operation: {
          label: t('common.operation'),
          value: 'operation',
          align: 'center'
        }
      },
      [
        'order',
        'name',
        'count',
        'operator',
        'uploadAt',
        'status',
        ...(writableEdit ? ['operation'] as const : [])
      ],
      rows,
      'id'
    )
  }, [rows, t, writableEdit])

  useEffect(() => {
    const subscription = timer(0, 10000).subscribe(() => { reload() })
    return () => { subscription.unsubscribe() }
  }, [reload])

  return (
    <Grid item>
      <LoadingAndErrorFrame { ...pageFlow.status }>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Box display="flex" flexDirection="row" alignItems="center">
              {`${t('common.currentQueueWithColon')}${list.length}`}
              { list.length > 0 && (<span style={{ marginLeft: 20 }}>{`${t('common.failedWithColon')}${failedList.length}`}</span>)}
            </Box>
          </Grid>
          {writableEdit && (
            <Grid item>
              <Button
                component={Link}
                to={Path.MANUAL_DEPSOT_CREATE}
                className={commonClasses.purpleGradualButton}
              >
                {t('page.createManualDeposit')}
              </Button>
            </Grid>
          )}
        </Grid>
        <Box marginTop={2}>
          <CoreTable
            classes={tableClasses}
            data={data}
            total={list.length}
            showNoData={false}
          />
        </Box>
      </LoadingAndErrorFrame>
    </Grid>
  )
}

export default React.memo(ManualDepositProcessTable)
